import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    Card,
    CardContent,
    Grid,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    TextField,
    IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useFormContext, useFormSubmission } from '../context/FormContext';
import { IMPACTION_TEETH } from './ImpactionConstants';
import { base64ToBlob } from '../utils/fileUtils';
import { fileToBase64 } from '../utils/fileUtils';

const ImpactionTab_Generate = () => {
    const { state, actions } = useFormContext();
    const { submitForm } = useFormSubmission();
    const [success, setSuccess] = useState(false);

    // Global impaction data
    const generalData = state.impactionData?.general || {};

    const handleImpressionChange = (e) => {
        actions.setImpactionData({
            ...state.impactionData,
            general: {
                ...generalData,
                impression: e.target.value
            }
        });
    };

    const handleImageUpload = async (key) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            try {
                const base64 = await fileToBase64(file);
                const fileData = { base64, name: file.name, type: file.type };
                actions.setImpactionData({
                    ...state.impactionData,
                    general: {
                        ...generalData,
                        [key]: fileData
                    }
                });
            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        };
        input.click();
    };

    const handleRemoveImage = (key) => {
        actions.setImpactionData({
            ...state.impactionData,
            general: {
                ...generalData,
                [key]: null
            }
        });
    };

    const handleGenerate = async () => {
        setSuccess(false);
        const result = await submitForm('/report/generate');
        if (result.success) {
            setSuccess(true);
        } else {
            console.error(result.error);
        }
    };

    // NEW: HTML Preview State
    const [previewHtml, setPreviewHtml] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const handlePreview = async () => {
        setPreviewLoading(true);
        setPreviewHtml(null);
        try {
            const formData = new FormData();

            // Deep clone to modify safely
            const payload = JSON.parse(JSON.stringify({
                patientData: state.patientData,
                impactionData: state.impactionData
            }));

            // Recursive function to strip ANY base64 string to avoid size limits
            const processData = (obj) => {
                if (!obj || typeof obj !== 'object') return;

                Object.keys(obj).forEach(key => {
                    // Check if the KEY itself is base64
                    if (key === 'base64') {
                        delete obj[key];
                        // Try to mark parent? Hard to access parent here without ref.
                        return;
                    }

                    const value = obj[key];

                    if (value && typeof value === 'object') {
                        // Check if it has base64 property
                        if ('base64' in value) {
                            delete value.base64;
                            value.isImagePlaceholder = true; // Mark as placeholder
                        }
                        // Recurse
                        processData(value);
                    }
                });
            };

            processData(payload);

            const jsonString = JSON.stringify(payload);
            console.log(`Payload size after stripping: ${(jsonString.length / 1024).toFixed(2)} KB`);

            // Use 'json_payload' to trigger the robust backend logic
            formData.append('json_payload', jsonString);

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/impaction_report/preview_html`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                // If it looks like HTML error (like the one from our backend), use it or extract text
                // But simplified: throw the text
                throw new Error(`Server Error (${response.status}): ${errorText.substring(0, 200)}...`);
            }

            const htmlText = await response.text();
            setPreviewHtml(htmlText);

        } catch (error) {
            console.error("Preview failed:", error);
            actions.setError(`Preview failed: ${error.message}`);
        } finally {
            setPreviewLoading(false);
        }
    };



    // Generic check for data completion in a sub-object
    const isSectionComplete = (data) => data && Object.keys(data).length > 0;

    // Calculate completion status
    const getCompletionStatus = () => {
        const patientDataCompleted = !!state.patientData;

        const teethStatus = IMPACTION_TEETH.reduce((acc, tooth) => {
            const data = state.impactionData?.[tooth] || {};

            // Check specific tabs
            const positionComplete = isSectionComplete(data.position) && !!data.position.type; // Check if type is selected
            const rootsComplete = isSectionComplete(data.roots);
            const iacComplete = isSectionComplete(data.iac);
            const adjacentComplete = isSectionComplete(data.adjacent);

            // A tooth is "started" if any data exists
            const isStarted = positionComplete || rootsComplete || iacComplete || adjacentComplete;

            // A tooth is "complete" if all tabs have data (heuristic)
            const isComplete = positionComplete && rootsComplete && iacComplete && adjacentComplete;

            acc[tooth] = {
                position: positionComplete,
                roots: rootsComplete,
                iac: iacComplete,
                adjacent: adjacentComplete,
                isStarted,
                isComplete
            };
            return acc;
        }, {});

        // Calculate total stats
        // Total sections = Patient Data + (4 tabs * 2 teeth) = 9 items
        const totalItems = 1 + (IMPACTION_TEETH.length * 4);

        let completedItems = patientDataCompleted ? 1 : 0;
        IMPACTION_TEETH.forEach(tooth => {
            const t = teethStatus[tooth];
            if (t.position) completedItems++;
            if (t.roots) completedItems++;
            if (t.iac) completedItems++;
            if (t.adjacent) completedItems++;
        });

        const percentage = Math.round((completedItems / totalItems) * 100);

        return {
            patientData: patientDataCompleted,
            teeth: teethStatus,
            completed: completedItems,
            total: totalItems,
            percentage
        };
    };

    const completionStatus = getCompletionStatus();

    // Data Summary
    const patientName = state.patientData?.PatientName || 'Not available';
    const patientAge = state.patientData?.Age || 'Not available';
    const patientGender = state.patientData?.Gender || 'Not available';
    const reportDate = new Date().toLocaleDateString();

    const renderImagePreview = (label, key, fileData, height = '150px') => {
        const preview = fileData ? fileData.base64 : null;
        return (
            <Box sx={{ width: '100%', maxWidth: '300px', mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>{label}</Typography>
                <Box sx={{
                    position: 'relative',
                    height: height,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    overflow: 'hidden'
                }}>
                    {preview ? (
                        <>
                            <img src={preview} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveImage(key)}
                                sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'rgba(255,255,255,0.8)' }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </>
                    ) : (
                        <Typography variant="caption" color="text.secondary">No Image</Typography>
                    )}
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ mt: 1 }}
                    onClick={() => handleImageUpload(key)}
                >
                    {preview ? "Change" : "Upload"}
                </Button>
            </Box>
        );
    };

    if (!state.patientData) {
        return (
            <Box p={3}>
                <Alert severity="warning">
                    Please load patient data first from the sidebar.
                </Alert>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom color="primary">
                Generate Impaction Report
            </Typography>

            {/* Completion Status */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Report Completion Status
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        {completionStatus.completed} of {completionStatus.total} sections completed ({completionStatus.percentage}%)
                    </Typography>

                    <Grid container spacing={4}>
                        {/* Patient Data Status */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>General</Typography>
                            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                                {completionStatus.patientData ?
                                    <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 20 }} /> :
                                    <ErrorIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                                }
                                <Typography variant="body2">Patient Data</Typography>
                            </Box>
                        </Grid>

                        {/* Per-Tooth Breakdown */}
                        {IMPACTION_TEETH.map(tooth => (
                            <Grid item xs={12} md={4} key={tooth}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Tooth {tooth} Status</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                                    <Box display="flex" alignItems="center">
                                        {completionStatus.teeth[tooth].position ?
                                            <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 18 }} /> :
                                            <WarningIcon color="action" sx={{ mr: 1, fontSize: 18 }} />
                                        }
                                        <Typography variant="body2">Position & Development</Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center">
                                        {completionStatus.teeth[tooth].roots ?
                                            <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 18 }} /> :
                                            <WarningIcon color="action" sx={{ mr: 1, fontSize: 18 }} />
                                        }
                                        <Typography variant="body2">Roots Assessment</Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center">
                                        {completionStatus.teeth[tooth].iac ?
                                            <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 18 }} /> :
                                            <WarningIcon color="action" sx={{ mr: 1, fontSize: 18 }} />
                                        }
                                        <Typography variant="body2">IAC Relationship</Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center">
                                        {completionStatus.teeth[tooth].adjacent ?
                                            <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 18 }} /> :
                                            <WarningIcon color="action" sx={{ mr: 1, fontSize: 18 }} />
                                        }
                                        <Typography variant="body2">Adjacent & Ramus</Typography>
                                    </Box>

                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* Data Summary */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Report Summary
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Patient Information</Typography>
                            <Typography variant="body2"><strong>Name:</strong> {patientName}</Typography>
                            <Typography variant="body2"><strong>Age/Gender:</strong> {patientAge} / {patientGender}</Typography>
                            <Typography variant="body2"><strong>Date:</strong> {reportDate}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Selected Teeth</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                {IMPACTION_TEETH.map(tooth => (
                                    completionStatus.teeth[tooth] && (
                                        <Chip key={tooth} label={`Tooth ${tooth}`} color="primary" size="small" variant="outlined" />
                                    )
                                ))}
                                {Object.values(completionStatus.teeth).every(v => !v) && (
                                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>No teeth data entered yet.</Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Radiographic Impression & Image */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Radiographic Impression
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                label="Impression / Summary"
                                placeholder="Enter final radiographic impression summary here..."
                                value={generalData.impression || ''}
                                onChange={handleImpressionChange}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {renderImagePreview("Impression Diagram / Image", "impressionImage", generalData.impressionImage)}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Generate Interaction */}
            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={handlePreview}
                            disabled={previewLoading || state.loading}
                            sx={{ px: 3, py: 1.5, fontSize: '1rem' }}
                        >
                            {previewLoading ? 'Generating Preview...' : 'Preview HTML Report'}
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleGenerate}
                            disabled={state.loading || previewLoading}
                            startIcon={state.loading ? null : <DownloadIcon />}
                            sx={{ px: 3, py: 1.5, fontSize: '1rem' }}
                        >
                            {state.loading ? 'Downloading...' : 'Generate & Download DOCX'}
                        </Button>
                    </Box>

                    {previewHtml && (
                        <Box sx={{ mt: 4, border: '1px solid #ccc', bgcolor: 'white', p: 1, height: '800px', overflow: 'auto' }}>
                            <Typography variant="subtitle2" sx={{ textAlign: 'left', mb: 1, color: 'text.secondary' }}>
                                Report Preview
                            </Typography>
                            <iframe
                                srcDoc={previewHtml}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                title="Report Preview"
                            />
                        </Box>
                    )}


                    {success && (
                        <Alert severity="success" sx={{ mt: 3, maxWidth: '600px', mx: 'auto' }}>
                            <Typography variant="subtitle1" fontWeight="bold">Success!</Typography>
                            Report generated and downloaded.
                        </Alert>
                    )}

                    {state.error && (
                        <Alert severity="error" sx={{ mt: 3, maxWidth: '600px', mx: 'auto' }}>
                            {state.error}
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Tips Section */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                    💡 Tips:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
                    <li>Verify all measurements for each impacted tooth.</li>
                    <li>Ensure relevant images are uploaded for Position, Roots, and Adjacent tabs.</li>
                    <li>The radiographic impression summary will appear at the end of the report.</li>
                </Typography>
            </Box>
        </Box>
    );
};

export default ImpactionTab_Generate;
